/**
 * Registry de Widgets para Aurelio Builder
 * 
 * @description Sistema centralizado para registro, gestión y acceso a todos los widgets
 * disponibles en el editor. Permite agregar nuevos widgets de manera modular y escalable.
 */

import { ELEMENT_TYPES, WIDGET_CATEGORIES } from '../../../constants/elementTypes.js';

class WidgetRegistryClass {
  constructor() {
    this.widgets = new Map();
    this.categories = new Map();
    this.initialized = false;
  }

  /**
   * Registrar un nuevo widget en el sistema
   * 
   * @param {Object} widget - Configuración del widget
   * @param {string} widget.type - Tipo único del widget
   * @param {string} widget.name - Nombre mostrado al usuario
   * @param {React.Component} widget.icon - Icono del widget
   * @param {string} widget.category - Categoría del widget
   * @param {Object} widget.defaultProps - Propiedades por defecto
   * @param {React.Component} widget.Renderer - Componente de renderizado
   * @param {Object} widget.Properties - Componentes de propiedades
   */
  register(widget) {
    if (!widget.type) {
      throw new Error('Widget must have a type');
    }

    if (this.widgets.has(widget.type)) {
      console.warn(`🔄 Widget ${widget.type} already registered, overwriting...`);
    }

    // Validar estructura del widget
    this.validateWidget(widget);

    // Registrar widget
    this.widgets.set(widget.type, widget);

    // Organizar por categoría
    const category = widget.category || WIDGET_CATEGORIES.BASIC;
    if (!this.categories.has(category)) {
      this.categories.set(category, []);
    }
    
    const categoryWidgets = this.categories.get(category);
    const existingIndex = categoryWidgets.findIndex(w => w.type === widget.type);
    
    if (existingIndex >= 0) {
      categoryWidgets[existingIndex] = widget;
    } else {
      categoryWidgets.push(widget);
    }

    console.log(`✅ Widget registered: ${widget.name} (${widget.type}) in category ${category}`);
  }

  /**
   * Obtener widget por tipo
   * 
   * @param {string} type - Tipo del widget
   * @returns {Object|null} Widget o null si no existe
   */
  get(type) {
    const widget = this.widgets.get(type);
    if (!widget) {
      console.warn(`⚠️ Widget ${type} not found in registry`);
      return null;
    }
    return widget;
  }

  /**
   * Obtener todos los widgets registrados
   * 
   * @returns {Array<Object>} Array de todos los widgets
   */
  getAll() {
    return Array.from(this.widgets.values());
  }

  /**
   * Obtener widgets por categoría
   * 
   * @param {string} category - Categoría a filtrar
   * @returns {Array<Object>} Array de widgets en la categoría
   */
  getByCategory(category) {
    return this.categories.get(category) || [];
  }

  /**
   * Obtener todas las categorías disponibles
   * 
   * @returns {Array<string>} Array de nombres de categorías
   */
  getCategories() {
    return Array.from(this.categories.keys());
  }

  /**
   * Obtener mapa completo de categorías con widgets
   * 
   * @returns {Object} Objeto con categorías como keys y arrays de widgets como values
   */
  getCategorizedWidgets() {
    const categorized = {};
    this.categories.forEach((widgets, category) => {
      categorized[category] = widgets;
    });
    return categorized;
  }

  /**
   * Verificar si un widget está registrado
   * 
   * @param {string} type - Tipo del widget
   * @returns {boolean} True si el widget está registrado
   */
  has(type) {
    return this.widgets.has(type);
  }

  /**
   * Desregistrar un widget
   * 
   * @param {string} type - Tipo del widget a desregistrar
   * @returns {boolean} True si se desregistró exitosamente
   */
  unregister(type) {
    const widget = this.widgets.get(type);
    if (!widget) {
      return false;
    }

    // Remover de widgets
    this.widgets.delete(type);

    // Remover de categoría
    const category = widget.category || WIDGET_CATEGORIES.BASIC;
    const categoryWidgets = this.categories.get(category);
    if (categoryWidgets) {
      const index = categoryWidgets.findIndex(w => w.type === type);
      if (index >= 0) {
        categoryWidgets.splice(index, 1);
      }
    }

    console.log(`🗑️ Widget unregistered: ${type}`);
    return true;
  }

  /**
   * Validar estructura de un widget
   * 
   * @param {Object} widget - Widget a validar
   * @throws {Error} Si el widget no tiene la estructura correcta
   */
  validateWidget(widget) {
    const requiredFields = ['type', 'name', 'icon', 'defaultProps'];
    
    for (const field of requiredFields) {
      if (!widget[field]) {
        throw new Error(`Widget is missing required field: ${field}`);
      }
    }

    if (!widget.Renderer && !widget.Properties) {
      throw new Error('Widget must have at least a Renderer or Properties component');
    }

    // Validar que el tipo esté definido en ELEMENT_TYPES
    if (!Object.values(ELEMENT_TYPES).includes(widget.type)) {
      console.warn(`⚠️ Widget type ${widget.type} is not defined in ELEMENT_TYPES`);
    }
  }

  /**
   * Obtener estadísticas del registry
   * 
   * @returns {Object} Estadísticas del registry
   */
  getStats() {
    return {
      totalWidgets: this.widgets.size,
      categories: this.categories.size,
      widgetsByCategory: Object.fromEntries(
        Array.from(this.categories.entries()).map(([category, widgets]) => [
          category,
          widgets.length
        ])
      )
    };
  }

  /**
   * Resetear el registry (útil para testing)
   */
  reset() {
    this.widgets.clear();
    this.categories.clear();
    this.initialized = false;
    console.log('🔄 Widget registry reset');
  }

  /**
   * Inicializar registry con widgets por defecto
   */
  initialize() {
    if (this.initialized) {
      console.warn('⚠️ Widget registry already initialized');
      return;
    }

    console.log('🚀 Initializing Widget Registry...');
    
    // Los widgets se auto-registrarán cuando se importen
    // Este método está disponible para inicialización manual si es necesario
    
    this.initialized = true;
    console.log('✅ Widget Registry initialized');
  }
}

// Instancia singleton del registry
export const WidgetRegistry = new WidgetRegistryClass();

// Exportar también la clase para testing
export { WidgetRegistryClass };

// Auto-inicializar el registry
WidgetRegistry.initialize();
